import React from "react";
import {Document, Font, Image, Page, Text, View} from "@react-pdf/renderer";
import {PlanSummaryItem} from "../../../../../../models/PlanSummaryItem";
import {DataTableCell, Table, TableBody, TableCell, TableHeader} from "@david.kucsai/react-pdf-table";
import {useTranslation} from "react-i18next";
import {formatLabor} from "../../../../../../utils/TextUtils";
import FitTime from "../../../../../../models/FitTime";
import {DrawingFileInfo} from "./util";
import {DrawingType, KeySettingsType, UnitsType} from "../../../../../../models/enums";
import {SymbolPreview} from "./symbol-preview/SymbolPreview";
import {formatDeduction, formatMarkupQty, formatValue} from "../../../../../../utils/SummaryUtils";
import UserSettings from "../../../../../../models/UserSettings";
import {useBreakAll} from "./useBreakAll";
import Roboto from '../../../../../../assets/fonts/Roboto-Regular.ttf';
import RobotoBold from '../../../../../../assets/fonts/Roboto-Bold.ttf';

Font.register({
    family: 'Roboto',
    fonts: [
        { src: Roboto },
        { src: RobotoBold, fontWeight: 'bold' },
    ],
});

const pdfStyles = {
	page: {
		padding: '1cm',
		fontFamily: 'Roboto',
		fontSize: 10,
		color: '#333',
	},
	h1: {
		fontSize: 18,
		fontWeight: 'bold',
		marginBottom: 10,
	},
	tableHeader: {
		fontFamily: 'Roboto',
		fontSize: 11,
		backgroundColor: '#f0f0f0',
	},
	pageNumberContainer: {
		position: 'absolute',
		bottom: 30,
		right: 30,
	},
	pageNumberText: {
		fontSize: 10,
		color: 'grey',
	},
} as const;


interface DrawingSummaryPDFProps {
	items: PlanSummaryItem[];
	fitTimes: FitTime[];
	drawingFileInfo: DrawingFileInfo
	drawingType: DrawingType
	settings: UserSettings | undefined
}

export const DrawingSummaryPDF: React.FC<DrawingSummaryPDFProps> = ({
	items, fitTimes, drawingFileInfo, drawingType, settings
}) => {
	const {t} = useTranslation();
	const breakAll = useBreakAll()
	const unitType = settings?.measurementSystem ?? UnitsType.METRIC
	const isKeySettingsDetailed = (settings?.keySettings ?? KeySettingsType.DETAILED) === KeySettingsType.DETAILED
	const isDrawingTakeoffType = drawingType === DrawingType.TAKE_OFF;

	const imagePadding = 10;
	const pageWidth = drawingFileInfo.width + imagePadding + imagePadding
	const pageHeight = drawingFileInfo.height + imagePadding // + 90

	// const drawingTypeLabel = drawingType === DrawingType.TAKE_OFF
	// 	? t("projects.summary.summaryPdfTableColumns.takeoff")
	// 	: t("projects.summary.summaryPdfTableColumns.asBuilt")

	return (
		<Document>
			<Page size={[pageWidth, pageHeight]} style={pdfStyles.page}>
				<Image
					style={{
						height: drawingFileInfo.height,
						paddingTop: imagePadding,
						paddingLeft: imagePadding,
						paddingRight: imagePadding
					}}
					src={drawingFileInfo.url}
				/>
			</Page>
			<Page size="A4" orientation="landscape" break={false} style={pdfStyles.page}>
				<Table data={items}>
					<TableHeader>
						{isKeySettingsDetailed ?
							<TableCell weighting={0.2}>
								{t("projects.summary.summaryPdfTableColumns.partNumber")}
							</TableCell> : null}

						<TableCell weighting={!isKeySettingsDetailed ? 0.6 : isDrawingTakeoffType ? 0.3 : 0.5}>
							{t("projects.summary.summaryPdfTableColumns.name")}
						</TableCell>
						{isDrawingTakeoffType && isKeySettingsDetailed ?
							<TableCell weighting={0.1}>
								{t("projects.summary.summaryPdfTableColumns.labour")}
							</TableCell> : null}
						{isDrawingTakeoffType && isKeySettingsDetailed?
							<TableCell weighting={0.1}>
								{t("projects.summary.summaryPdfTableColumns.fitTimeMultiplier")}
							</TableCell> : null}
						{isKeySettingsDetailed ?
							<TableCell weighting={0.1}>
								{t("projects.summary.summaryPdfTableColumns.markUpQty")}
							</TableCell> : null }
						{isKeySettingsDetailed ?
							<TableCell weighting={0.1}>
								{t("projects.summary.summaryPdfTableColumns.deduction")}
							</TableCell> : null }
						<TableCell weighting={isKeySettingsDetailed ? 0.1 : 0.2}>
							{t("projects.summary.summaryPdfTableColumns.quantity")}
						</TableCell>

						<TableCell weighting={isKeySettingsDetailed ? 0.1 : 0.2}>
							{t("projects.summary.summaryPdfTableColumns.symbol")}
						</TableCell>
					</TableHeader>
					<TableBody fontSize={10} textAlign={"center"}>
						{isKeySettingsDetailed ?
							<DataTableCell weighting={0.2} getContent={(item) =>
								<Text {...breakAll}>{item.partNumber}</Text>}
							/> : null }
						<DataTableCell weighting={!isKeySettingsDetailed ? 0.6 : isDrawingTakeoffType ? 0.3 : 0.5} getContent={(item) =>
							<Text {...breakAll}>{item.name}</Text>}
						/>
						{isDrawingTakeoffType && isKeySettingsDetailed ?
							<DataTableCell weighting={0.1} getContent={(item) => formatLabor(item.laborTime)}/>
							: null}
						{isDrawingTakeoffType && isKeySettingsDetailed ?
							<DataTableCell weighting={0.1}
										   getContent={(item) => {
											   const fitTime = fitTimes.find(ft => ft.id === item.fitTimeId);
											   return fitTime?.getFormattedMultiplier() ?? "-";
										   }}/>
							: null}
						{isKeySettingsDetailed ?
							<DataTableCell weighting={0.1} getContent={(item) => formatMarkupQty(item, unitType)}/>
							: null }
						{isKeySettingsDetailed ?
							<DataTableCell weighting={0.1} getContent={(item) => formatDeduction(item, unitType)}/>
							: null }
						<DataTableCell weighting={isKeySettingsDetailed ? 0.1 : 0.2} getContent={(item) => formatValue(item, unitType)}/>
						<DataTableCell weighting={isKeySettingsDetailed ? 0.1 : 0.2} getContent={(item) => <SymbolPreview item={item}/>}/>
					</TableBody>
				</Table>
				<View style={pdfStyles.pageNumberContainer} fixed>
					<Text
						style={pdfStyles.pageNumberText}
						render={({ pageNumber, totalPages }) => `${pageNumber} / ${totalPages}`}
					/>
				</View>

			</Page>
		</Document>
	);
};



